import type { ButtonHTMLAttributes, Ref } from "react";
import type { LucideIcon } from "lucide-react";
import { Icon } from "./Icon";
import { cx } from "./cx";

export type ButtonVariant = "primary" | "secondary" | "outline" | "quiet" | "machine" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** primary = green (default action) · secondary = blue (info) · machine = steel (parts contexts only) */
  variant?: ButtonVariant;
  /** 32 / 40 / 48px, from --control-sm/md/lg. */
  size?: ButtonSize;
  /** Lucide icon shown before the label. */
  icon?: LucideIcon;
  /** Lucide icon shown after the label. */
  iconRight?: LucideIcon;
  fullWidth?: boolean;
  /** The inner button, for moving focus to it. */
  ref?: Ref<HTMLButtonElement>;
}

const SIZE: Record<ButtonSize, string> = {
  sm: "h-(--control-sm) px-3 [font-size:var(--text-sm)]",
  md: "h-(--control-md) px-[18px] [font-size:var(--text-base)]",
  lg: "h-(--control-lg) px-6 [font-size:var(--text-md)]",
};

// Hover darkens one step, press darkens a second step. `enabled:` keeps a disabled
// button from reacting. Colour and shadow snap; only the 1px press is animated.
const VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-green-700 text-white border-green-700 enabled:hover:bg-green-800 enabled:hover:shadow-brand enabled:active:bg-green-900",
  secondary: "bg-blue-800 text-white border-blue-800 enabled:hover:bg-blue-900 enabled:active:bg-blue-900",
  outline: "bg-transparent text-green-800 border-green-700 enabled:hover:bg-green-50 enabled:active:bg-green-100",
  quiet: "bg-transparent text-ink-700 border-transparent enabled:hover:bg-paper-2 enabled:active:bg-paper-3",
  machine: "bg-steel-600 text-white border-steel-600 enabled:hover:bg-steel-700 enabled:active:bg-ink-800",
  danger: "bg-danger-500 text-white border-danger-500 enabled:hover:bg-danger-700 enabled:active:bg-danger-700",
};

/** Primary action control. Sentence-case labels, one primary per view. */
export function Button({ variant = "primary", size = "md", icon, iconRight, fullWidth, className, children, ...rest }: ButtonProps) {
  const iconSize = size === "lg" ? 20 : 18;
  return (
    <button
      className={cx(
        "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm border",
        "type-label font-bold tracking-[0.01em]",
        "cursor-pointer [transition:var(--transition-control)] enabled:active:translate-y-px",
        "disabled:cursor-not-allowed disabled:border-paper-3 disabled:bg-paper-2 disabled:text-ink-300",
        SIZE[size],
        VARIANT[variant],
        fullWidth && "w-full",
        className,
      )}
      {...rest}
    >
      {icon && <Icon icon={icon} size={iconSize} />}
      {/* One span around the label: a bare text node beside an interpolation becomes its own
          flex item, and the gap then lands inside the label ("Pay ₱ 3,119"). */}
      <span>{children}</span>
      {iconRight && <Icon icon={iconRight} size={iconSize} />}
    </button>
  );
}
